'use client';

import { useEffectOnce } from '@carvana/merch-react-hooks';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import type React from 'react';

type VdpNavigationContextType = {
  dismissVdpNavigation: () => void;
  showVdpSkeleton: boolean;
  triggerVdpNavigation: () => void;
};

const VdpNavigationContext = createContext<VdpNavigationContextType>({
  dismissVdpNavigation: () => {},
  showVdpSkeleton: false,
  triggerVdpNavigation: () => {}
});

export function VdpNavigationContextProvider({
  children
}: React.PropsWithChildren) {
  const [showVdpSkeleton, setShowVdpSkeleton] = useState(false);

  const dismissVdpNavigation = useCallback(() => setShowVdpSkeleton(false), []);
  const triggerVdpNavigation = useCallback(() => setShowVdpSkeleton(true), []);

  useEffectOnce(() => {
    if (typeof window === 'undefined') return;

    function handleRestore() {
      setShowVdpSkeleton(false);
    }

    window.addEventListener('popstate', handleRestore);
    window.addEventListener('pageshow', handleRestore);

    return () => {
      window.removeEventListener('popstate', handleRestore);
      window.removeEventListener('pageshow', handleRestore);
    };
  }, []);

  const value = useMemo(
    () => ({ dismissVdpNavigation, showVdpSkeleton, triggerVdpNavigation }),
    [dismissVdpNavigation, showVdpSkeleton, triggerVdpNavigation]
  );

  return (
    <VdpNavigationContext.Provider value={value}>
      {children}
    </VdpNavigationContext.Provider>
  );
}

export function useDismissVdpNavigation() {
  return useContext(VdpNavigationContext).dismissVdpNavigation;
}

export function useShowVdpSkeleton() {
  return useContext(VdpNavigationContext).showVdpSkeleton;
}

export function useTriggerVdpNavigation() {
  return useContext(VdpNavigationContext).triggerVdpNavigation;
}
